/**
 * Admin API Client for Frontend
 * Makes requests to Cloudflare tunnel backend which uses Shopify Admin API
 */

// const API_BASE_URL = 'http://localhost:3001'; // Local development
const API_BASE_URL = 'https://shark-app-vvgah.ondigitalocean.app'; // Production DigitalOcean

class AdminApiClient {
    constructor() {
        this.customerId = null;
    }

    /**
     * Initialize with customer ID from session
     */
    async init() {
        // Get customer ID from Liquid template data
        const dataElement = document.getElementById('habit-tracker-data');
        
        if (dataElement) {
            try {
                const sessionData = JSON.parse(dataElement.textContent);
                
                if (sessionData.customer?.id) {
                    const rawId = sessionData.customer.id;
                    // Extract numeric ID from GID
                    this.customerId = rawId.toString().replace('gid://shopify/Customer/', '');
                    console.log('� Admins API Client initialized with customer ID:', this.customerId);
                } else {
                    console.warn('⚠️ No customer ID found in session data');
                }
            } catch (e) {
                console.error('❌ Failed to parse session data:', e);
            }
        } else {
            console.error('❌ habit-tracker-data element not found in DOM');
        }
        
        return this;
    }

    /**
     * Make API request to backend
     */
    async request(endpoint, data = {}) {
        try {
            const requestPayload = {
                customerId: this.customerId,
                ...data
            };

            console.log(`🔄 Making request to ${API_BASE_URL}${endpoint}`, requestPayload);

            const response = await fetch(`${API_BASE_URL}${endpoint}`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify(requestPayload),
                cache: 'no-store', // Disable caching
            });

            console.log(`📡 Response status: ${response.status}`);

            if (!response.ok) {
                let errorData;
                try {
                    errorData = await response.json();
                } catch (e) {
                    const errorText = await response.text();
                    errorData = { 
                        error: `Request failed: ${response.status}`,
                        details: errorText || 'No error details available'
                    };
                }
                console.error(`❌ API error (${response.status}):`, errorData);
                throw new Error(errorData.error || `Request failed: ${response.status}`);
            }

            const result = await response.json();
            console.log(`✅ API response:`, result);
            return result;
        } catch (error) {
            console.error(`❌ API request failed for ${endpoint}:`, error);
            console.error(`❌ Error type:`, error.constructor.name);
            console.error(`❌ Error message:`, error.message);
            
            // Check if it's a network error (backend not running)
            if (error.message.includes('Failed to fetch') || error.message.includes('fetch failed') || error.message.includes('NetworkError')) {
                console.error('⚠️ Network error - Backend server may not be accessible at', API_BASE_URL);
                console.error('⚠️ Check: 1) Server is running 2) CORS is configured 3) URL is correct');
            }
            throw error;
        }
    }

    /**
     * Get habit tracker data
     */
    async getHabitTracker() {
        return await this.request('/api/habit-tracker');
    }

    /**
     * Complete a workout
     */
    async completeWorkout(date, videoId) {
        console.log('� DCompleting workout:', { date, videoId });
        return await this.request('/api/complete-workout', { date, videoId });
    }

    /**
     * Check subscription status
     */
    async checkSubscription() {
        return await this.request('/api/check-subscription');
    }

    /**
     * Get all badges
     */
    async getBadges() {
        return await this.request('/api/badges');
    }
}

// Export singleton instance
export const adminApi = new AdminApiClient();
